"use client";

import { useCallback, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useProfilePreviewStore } from "../model/profile-preview-store";
import type { ProfilePreviewData } from "../model/types";
import { useUserPresence } from "@/shared/lib/hooks/use-user-presence";
import { userApi, type UserProfile } from "@/shared/api/user";

export type UserStatus = "online" | "offline" | "playing" | "busy" | "away" | "searching";

export function useEffectiveStatus(profile: ProfilePreviewData | null): UserStatus {
  return useUserPresence(profile).status as UserStatus;
}

const PREVIEW_HOVER_DELAY_MS = 350;
const PREVIEW_PIN_DELAY_MS = 3000;
const STALE_TIME_MS = 90_000;

function getProfileQueryKey(userId: string | null, username: string | null) {
  if (userId) return ["profile-preview", "id", userId];
  if (username) return ["profile-preview", "username", username];
  return ["profile-preview", "none"];
}

export function useProfilePreviewData(userId: string | null, username: string | null) {
  const fetchProfile = useCallback(async (): Promise<UserProfile | null> => {
    if (userId) return userApi.getUserById(userId);
    if (username) return userApi.getUserByUsername(username);
    return null;
  }, [userId, username]);

  const enabled = !!(userId || username);
  const query = useQuery({
    queryKey: getProfileQueryKey(userId, username),
    queryFn: fetchProfile,
    enabled,
    staleTime: STALE_TIME_MS,
    gcTime: STALE_TIME_MS * 2,
  });

  return {
    profile: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}

export function useProfilePreviewPinTimer(
  onPin: () => void,
  active: boolean,
  isPinned: boolean,
) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!active || isPinned) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onPin();
    }, PREVIEW_PIN_DELAY_MS);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [active, isPinned, onPin]);
}

export function useProfilePreviewClickOutside(modalRef: React.RefObject<HTMLElement | null>) {
  const close = useProfilePreviewStore((s) => s.close);
  const state = useProfilePreviewStore((s) => s.state);

  useEffect(() => {
    if (state !== "pinned") return;
    const handleClick = (e: MouseEvent) => {
      const el = modalRef.current;
      if (!el || !(e.target instanceof Node) || el.contains(e.target)) return;
      close();
    };
    document.addEventListener("mousedown", handleClick, true);
    return () => document.removeEventListener("mousedown", handleClick, true);
  }, [state, close, modalRef]);
}

export { PREVIEW_HOVER_DELAY_MS, PREVIEW_PIN_DELAY_MS };
